<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.editPackage") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #008274">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <ValidationObserver v-slot="{ invalid, validate }">
        <form
          @submit.prevent="
            validate().then((valid) => valid && validateFormInputs())
          "
        >
          <div class="row">
            <!-- === Name AR === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.nameAr')"
              rules="required|arabicLettersAndNumbersOnly"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="text"
                :placeholder="$t('PLACEHOLDERS.nameAr')"
                @input="data.name_ar = allowArabicNumbersSymbols($event)"
                v-model.trim="data.name_ar"
                :errors="errors[0]"
                :col="12"
                required
              />
            </ValidationProvider>

            <!-- === Name EN === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.nameEn')"
              rules="required|englishLettersAndNumbersOnly"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="text"
                :placeholder="$t('PLACEHOLDERS.nameEn')"
                v-model.trim="data.name_en"
                :col="12"
                @input="data.name_en = allowEnglishNumbersSymbols($event)"
                :errors="errors[0]"
                required
              />
            </ValidationProvider>

            <!-- === Desc AR === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.descAr')"
              rules="arabicLettersAndNumbersOnly"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.descAr')"
                v-model.trim="data.descAr"
                :col="12"
                :errors="errors[0]"
              />
            </ValidationProvider>

            <!-- === Desc EN === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.descEn')"
              rules="englishLettersAndNumbersOnly"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.descEn')"
                v-model.trim="data.descEn"
                :col="12"
                :errors="errors[0]"
              />
            </ValidationProvider>

            <!-- === Price === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.price')"
              rules="required|decimal|positive"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="number"
                :placeholder="$t('PLACEHOLDERS.price')"
                v-model.number="data.price"
                :errors="errors[0]"
                :col="12"
                min="1"
                required
              />
            </ValidationProvider>

            <!-- === Price After Discount === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.price_after_discount')"
              :rules="`decimal|price_after_discount:${data.price}`"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="number"
                :col="12"
                :placeholder="$t('PLACEHOLDERS.price_after_discount')"
                v-model.number="data.price_after_discount"
                :errors="errors[0]"
                min="1"
              />
            </ValidationProvider>

            <!-- === Number of Available Bids === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.number_of_available_bids')"
              rules="required|min_value:1|decimal"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="number"
                :col="12"
                :placeholder="$t('PLACEHOLDERS.number_of_available_bids')"
                v-model.number="data.number_of_available_bids"
                :errors="errors[0]"
                min="1"
                required
              />
            </ValidationProvider>

            <!-- === Package Duration === -->
            <ValidationProvider
              :name="$t('PLACEHOLDERS.package_duration')"
              rules="required|min_value:1"
              v-slot="{ errors }"
              class="col-lg-6"
            >
              <base-input
                type="number"
                :col="12"
                :placeholder="$t('PLACEHOLDERS.package_duration')"
                v-model.number="data.package_duration"
                :errors="errors[0]"
                min="0"
                required
              />
            </ValidationProvider>

            <!-- === Submit Button === -->
            <div class="col-12 text-end">
              <base-button
                class="mt-2"
                styleType="primary_btn"
                :btnText="$t('BUTTONS.save')"
                :isLoading="isWaitingRequest"
                :disabled="invalid || isWaitingRequest"
              />
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>

    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import allowLanguageMinxin from "@/mixins/allowLanguageMinxins.js";

export default {
  name: "EditPackage",
  mixins: [allowLanguageMinxin],
  data() {
    return {
      isWaitingRequest: false,
      types: [],
      data: {
        name_ar: "",
        name_en: "",
        descAr: null,
        descEn: null,
        price: null,
        type: null,
        price_after_discount: null,
        number_of_available_bids: null,
        package_duration: null,
        is_active: null,
      },
      statusOptions: [
        { id: 1, name: this.$t("STATUS.active"), value: 1 },
        { id: 0, name: this.$t("STATUS.notActive"), value: 0 },
      ],
    };
  },
  methods: {
    // Start:: validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: validate Form Inputs
    async getTypes() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `estate-types?page=0&limit=0&status=1`,
        });
        this.types = res.data.data;
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("_method", "put");
      if (this.data.name_ar)
        REQUEST_DATA.append("name[ar]", this.data.name_ar ?? "");

      REQUEST_DATA.append("name[en]", this.data.name_en ?? "");
      REQUEST_DATA.append("description[ar]", this.data.descAr ?? "");
      REQUEST_DATA.append("description[en]", this.data.descEn ?? "");
      if (this.data.price !== null)
        REQUEST_DATA.append("price", this.data.price);

      if (this.data.type) {
        REQUEST_DATA.append("estate_type_id", this.data.type?.id);
      }
      if (this.data.price_after_discount !== null) {
        REQUEST_DATA.append(
          "price_after_discount",
          this.data.price_after_discount
        );
      }
      if (this.data.number_of_available_bids) {
        REQUEST_DATA.append("ads_number", this.data.number_of_available_bids);
      }

      REQUEST_DATA.append("duration", this.data.package_duration);

      REQUEST_DATA.append("is_active", this.data.is_active?.value);

      try {
        await this.$axios({
          method: "POST",
          url: `packages/${this.$route.params.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.editedSuccessfully"));
        this.$router.push({ path: "/packages/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Submit Form
    // start show data
    async showPackage() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `packages/${this.$route.params?.id}`,
        });
        this.data.name_ar = res.data.data.Package.trans.name.ar;
        this.data.name_en = res.data.data.Package.trans.name.en;
        this.data.descAr = res.data.data.Package.trans.description.ar;
        this.data.descEn = res.data.data.Package.trans.description.en;
        this.data.price = res.data.data.Package.price;
        console.log(res.data.data);
        this.data.type = res.data.data.Package.estate_type;
        this.data.price_after_discount =
          res.data.data.Package.price_after_discount;
        this.data.number_of_available_bids = res.data.data.Package.ads_number;
        this.data.package_duration = res.data.data.Package.duration;
        this.data.is_active = res.data.data.Package.is_active
          ? { id: 1, name: this.$t("STATUS.active"), value: 1 }
          : { id: 0, name: this.$t("STATUS.notActive"), value: 0 };
      } catch (error) {
        this.loading = false;
        console.log(error?.response?.data?.message);
      }
    },
    // end show data
  },
  created() {
    this.showPackage();
    this.getTypes();
  },
};
</script>
